import { ensureNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import {
	Mark,
	Node,
	Schema
} from "prosemirror-model";
import { Command, EditorState } from "prosemirror-state";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { addOrUpdateLinkAtSelection } from "../commands/addOrUpdateLinkAtSelection";
import { removeLinkAtSelection } from "../commands/removeLinkAtSelection";
import {
	EditLinkInitialModel,
	EditLinkResult,
	useEditLinkDialog
} from "../EditLinkDialog/EditLinkDialog";
import { LinkMarkAttributes } from "../schema/marks/link";
import {
	getNodesWithPositionFromSelection,
	getTextFromSelection,
	isMarkActive,
	NodeWithPosition
} from "../utils";
import { hasMarks } from "../schema";
import { CallbackWithSchemaResult, useExtendWithSchema } from "./useExtendWithSchema";

export type LinkToolHandlers =
| {
	isSupportedBySchema: true;
	editLinkDialog: ReactElement | null;
	isLink: boolean;
	addOrUpdateLinkHandler(): void;
	removeLinkHandler(): void;
} | {
	isSupportedBySchema: false;
};

export function useLinkTool(
	editorState: EditorState,
	applyCommand: (command: Command) => void,
	areHashUrlsAllowed: boolean,
	focus?: () => void
): LinkToolHandlers {
	const [
		editLinkDialog,
		openEditLinkDialog
	] = useEditLinkDialog();

	const isMarkActiveHandler = useExtendWithSchema(
		editorState.schema,
		schema => hasMarks(schema, ["link"]),
		useCallback(
			(schema: Schema<never, "link">, editorState: EditorState): boolean => isMarkActive(editorState, schema.marks.link),
			[]
		)
	);

	const addOrUpdateLinkHandler: CallbackWithSchemaResult<[], Promise<void>> = useExtendWithSchema(
		editorState.schema,
		schema => hasMarks(schema, ["link"]),
		useCallback(
			async (schema: Schema<never, "link">): Promise<void> => {
				const linkNodes: readonly NodeWithPosition[] = getNodesWithPositionFromSelection(
					editorState,
					{
						markType: schema.marks.link
					}
				);

				let initialData: EditLinkInitialModel;
				if (linkNodes.length === 1) {
					const [singleLinkNodeWithPosition] = linkNodes;
					const singleLinkNode: Node = singleLinkNodeWithPosition.node;
					const linkMark: Mark = ensureNotNil(
						singleLinkNode
							.marks
							.find(l => l.type === schema.marks.link)
					);

					const {
						href,
						openInANewWindow
					} = (linkMark.attrs as LinkMarkAttributes);

					initialData = {
						href,
						shouldOpenInNewWindow: openInANewWindow ?? true,
						text: singleLinkNode.textContent,
						doesSupportTextEditing: singleLinkNode.isText,
						areHashUrlsAllowed
					};
				} else {
					const text: string = getTextFromSelection(editorState);
					initialData = {
						text: getTextFromSelection(editorState),
						href: "",
						shouldOpenInNewWindow: true,
						doesSupportTextEditing: editorState.selection.empty || isNotNilOrEmpty(text),
						areHashUrlsAllowed
					};
				}

				const result: TryResult<EditLinkResult> = await openEditLinkDialog(initialData);
				if (!result.wasSuccessful) {
					return;
				}

				const resultData: EditLinkResult = result.result;
				applyCommand(
					addOrUpdateLinkAtSelection(
						schema,
						resultData,
						initialData.doesSupportTextEditing && initialData.text !== resultData.text
					)
				);
				focus?.();
			},
			[
				editorState,
				openEditLinkDialog,
				applyCommand,
				focus,
				areHashUrlsAllowed
			]
		)
	);

	const removeLinkHandler: CallbackWithSchemaResult<[], void> = useExtendWithSchema(
		editorState.schema,
		schema => hasMarks(schema, ["link"]),
		useCallback(
			(schema: Schema<never, "link">) => {
				applyCommand(removeLinkAtSelection(schema));
				focus?.();
			},
			[applyCommand, focus]
		)
	);

	return useMemo(
		(): LinkToolHandlers => {
			if (!addOrUpdateLinkHandler.isSupportedBySchema
				|| !removeLinkHandler.isSupportedBySchema
				|| !isMarkActiveHandler.isSupportedBySchema
			) {
				return {
					isSupportedBySchema: false
				};
			}

			return {
				isSupportedBySchema: true,
				addOrUpdateLinkHandler,
				removeLinkHandler,
				isLink: isMarkActiveHandler(editorState),
				editLinkDialog
			};
		},
		[
			addOrUpdateLinkHandler,
			editLinkDialog,
			editorState,
			isMarkActiveHandler,
			removeLinkHandler
		]
	);
}